// © Microsoft Corporation. All rights reserved.

import { CommonExperienceProps, TelemetryTags } from "@msnews/experiences-base";
import { FeedNavigationData, FeedNavigationItem } from "@msnews/feed-navigation";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";

import { AppError } from "@msnews/diagnostics";
import { AutoSuggestProperties } from "@msnews/autosuggest";
import { SettingsProps } from "@msnews/settings";
import { CommonHeaderClassNameContract } from "./CommonHeader.classnames";
import { HamburgerMenuItemInternal } from "@fast/components";
import { ManagedClasses } from "@microsoft/fast-components-class-name-contracts-msft";
import { MeControlComponentProps } from "@msnews/me-control-component";
import { CommonHeaderLayout, NavigationItemRenderInfo } from "./CommonHeader.config";
import { SearchBoxProps } from "@msnews/search-box";

/**
 * Telemetry item collection used to define telemetry in the common header
 */
export interface CommonHeaderTelemetryContext {
    hamburgerMenuTelemetry?: any;
    contextualNavTelemetry?: any;
    closeHamburgerMetaTag?: any;
    overflowMetaTag?: any;
    logoTag?: any;
    meControlTag?: any;
    settingsMetaTag?: any;
    settingsPersonalizeButtonMetaTag?: any;
    settingsCloseButtonMetaTag?: any;
    settingsLanguageSelectorMetaTag?: any;
    hamburgerMenuExpandedChangeTelemetryCallback?: (event: CustomEvent) => void;
    languageOptions?: any;
    personalizeButtonMetaTag?: any;
}

/**
 * An interface to represent the Common Header jss managed classes.
 */
export type CommonHeaderManagedClasses = ManagedClasses<CommonHeaderClassNameContract>;

/**
 * An interface to represent the CommonHeader handled props.
 */
export interface CommonHeaderHandledProps extends CommonExperienceProps, CommonHeaderManagedClasses {
    /** Props for hamburger menu component */
    hamburgerMenuProps?: HamburgerMenuData<HamburgerMenuItem>;

    /** Props for contextual nav component */
    contextualNavProps?: FeedNavigationData<ContextualNavItem>;

    /** Selected nav item */
    selectedNavItem?: FeedNavigationItem;

    /** Selected nav item */
    selectedNavMenuItem?: FeedNavigationItem;

    /** Props for contextual secondary nav component */
    contextualSecondaryNavProps?: FeedNavigationData<ContextualNavItem>;

    /** Selected secondary nav item */
    selectedSecondaryNavItem?: FeedNavigationItem;

    /** Selected secondary nav item */
    selectedSecondaryNavMenuItem?: FeedNavigationItem;

    /** Callback for handling a navigation item content rendering */
    handleRenderInfo?: (event: CustomEvent) => void;

    /** Props for search box component */
    searchProps?: SearchProps;

    /** Props for me control component */
    meControlProps?: MeControlComponentProps;

    /** Callback to navigate to "My Feed" */
    goToMyFeed?: () => void;

    /** Flag to remove the remove top Content of the hearder */
    disableFirstRow?: boolean;

    /** Flag to indicate if it is a windows shell experience */
    isWindowsShellExperience: boolean;

    /** Flag to indicate if we should hide secondary nav. Relevant only if secondary nav enabled */
    hideSecondaryNavAsL3InContent: boolean;

    /** Vertical Key from prime */
    primeOverrideVerticalKey: string;

    /** Handler to log app errors.  */
    appErrorCallback?: (error: AppError) => void;

    /** Flag to disable dark mode styles */
    disableDarkmode?: boolean;

    /** Flag to hide personalization button on feed navigation bar */
    hidePersonalizeButton?: boolean;

    /** Flag to wrap common header with DesignSystemProvider */
    includeWrapperDesignSystemProvider?: boolean;

    /** Settings dialog props */
    settingsProps?: SettingsProps;

    /** Flag to render new branding logo */
    showNewLogo?: boolean;

    /** Common header telemetry context */
    commonHeaderTelemetryContext?: CommonHeaderTelemetryContext;

    /** Microsoft logo alt text */
    logoAltText?: string;

    /** Is Secondary Nav Enabled */
    isContextualSecondaryNavEnabled: boolean;

    /** Common header layout */
    layout?: CommonHeaderLayout;
}

/**
 * Props for hamburger menu
 */
export interface HamburgerMenuData<T = HamburgerMenuItemInternal> {
    /** Tooltip for hamburger menu button */
    menuButtonTooltip: string;

    /** Tooltip for close button */
    closeButtonTooltip: string;

    /** List of nav items */
    navItems: T[];

    /** Selected nav id */
    selectedNavId: string;

    /** Flag to use mobile styles */
    useMobileStyles?: boolean;
}

/**
 * List of static telemetry identifiers
 */
export enum CommonHeaderTelemetry {
    searchName = "headersearch",
    searchInputName = "searchinput",
    searchWebName = "search_websearch"
}

/**
 * Telemetry context for the search box
 */
export interface SearchTelemetryContext {
    /** Reference to the search form telemetry object */
    formObject?: TelemetryObjectBase;

    /** Input telemetry */
    formTag?: TelemetryTags;

    /** Reference to the input telemetry object */
    inputObject?: TelemetryObjectBase;

    /** Input telemetry */
    inputTag?: string;

    /** Reference to the button telemetry object */
    buttonObject?: TelemetryObjectBase;

    /** Button telemetry metadata tag */
    buttonMetaTag?: string;
}

/**
 * Props for the search box component
 */
export interface SearchProps extends SearchBoxProps {
    /** Function to track telemetry calls */
    telemetryCallback?: (element: HTMLElement, action?: string, behavior?: number) => void;

    /** AutoSuggest properties */
    autoSuggest?: AutoSuggestProperties;

    /** Enable keypress capture */
    keyPressCaptureEnabled?: boolean;

    /** Request id */
    requestId: string;

    /** Telemetry Context */
    telemetryContext?: SearchTelemetryContext;

    /** Form code */
    "data-form-code"?: string;
}

export interface HamburgerMenuItem extends HamburgerMenuItemInternal {
    /** The telemetry metadata */
    telemetryMetadata?: any;

    /** Object for rendering info */
    renderInfo?: NavigationItemRenderInfo;

    /** Vertical Key value */
    verticalKey?: string;
}

export interface ContextualNavItem extends FeedNavigationItem {
    /** The telemetry metadata */
    telemetryMetadata?: any;

    /** Object for rendering info */
    renderInfo?: NavigationItemRenderInfo;

    /** Vertical Key value */
    verticalKey?: string;
}

/**
 * An interface to represent the CommonHeader unhandled props.
 */
export interface CommonHeaderUnhandledProps extends React.HTMLAttributes<HTMLDivElement> { }

/**
 * The CommonHeader props type
 */
export type CommonHeaderProps = CommonHeaderHandledProps & CommonHeaderUnhandledProps;